// useTimerState.js
//
// Единственный таймер на всё приложение: и на 90-секундный лимит SDMT
// (строгая шкала), и на 3/10-секундные таймеры тренажёра. UI у них
// разный — отсчёт цифрами в шкале и Lottie-кольцо в тренажёре, — но
// арифметика времени одна, и держать её надо в одном месте.
//
// Почему не setInterval со счётчиком: интервалы в неактивной вкладке
// Chrome троттлит до 1 раза в секунду и хуже, поэтому счётчик
// "прибавляю 100мс каждые 100мс" уезжает тем сильнее, чем дольше
// пациент не смотрит на вкладку. Здесь тик нужен ТОЛЬКО чтобы
// перерисовать цифры, а сколько времени реально прошло — всегда
// вычисляется из Date.now() относительно зафиксированного дедлайна.
// Поэтому пропущенные тики не искажают результат пробы.
import { useState, useRef, useEffect, useCallback } from 'react';

const DEFAULT_TICK_MS = 100;

export function useTimerState({ duration = 0, onExpire, tickMs = DEFAULT_TICK_MS, autoStart = false } = {}) {
  const [remainingMs, setRemainingMs] = useState(duration);
  const [isRunning, setIsRunning] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  const deadlineRef = useRef(0);
  const durationRef = useRef(duration);
  const pausedRemainingRef = useRef(duration);
  const expiredRef = useRef(false);
  const runningRef = useRef(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const onExpireRef = useRef(onExpire);

  // onExpire читаем через ref: колбэк прилетает из компонента задания и
  // пересоздаётся на каждый рендер, а пересоздавать из-за этого таймер
  // нельзя — он потеряет дедлайн.
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);

  const clearHandles = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  }, []);

  const fireExpire = useCallback(() => {
    if (expiredRef.current) return;
    expiredRef.current = true;
    runningRef.current = false;
    clearHandles();
    setIsRunning(false);
    setIsFinished(true);
    setRemainingMs(0);
    if (onExpireRef.current) onExpireRef.current();
  }, [clearHandles]);

  const check = useCallback(() => {
    const left = deadlineRef.current - Date.now();
    if (left <= 0) {
      fireExpire();
      return;
    }
    setRemainingMs(left);
  }, [fireExpire]);

  const arm = useCallback((ms) => {
    clearHandles();
    deadlineRef.current = Date.now() + ms;
    runningRef.current = true;
    setRemainingMs(ms);
    setIsRunning(true);
    setIsFinished(false);
    intervalRef.current = setInterval(check, tickMs);
    // Дублирующий setTimeout: в активной вкладке он срабатывает точнее
    // интервала, в фоновой — оба троттлятся одинаково, и тогда финиш
    // ловит check() по Date.now(). fireExpire идемпотентен.
    timeoutRef.current = setTimeout(fireExpire, ms);
  }, [check, clearHandles, fireExpire, tickMs]);

  const start = useCallback((overrideDuration) => {
    const ms = typeof overrideDuration === 'number' ? overrideDuration : durationRef.current;
    durationRef.current = ms;
    pausedRemainingRef.current = ms;
    expiredRef.current = false;
    arm(ms);
  }, [arm]);

  const pause = useCallback(() => {
    if (!intervalRef.current && !timeoutRef.current) return;
    const left = Math.max(0, deadlineRef.current - Date.now());
    pausedRemainingRef.current = left;
    runningRef.current = false;
    clearHandles();
    setIsRunning(false);
    setRemainingMs(left);
  }, [clearHandles]);

  const resume = useCallback(() => {
    if (expiredRef.current || isRunning) return;
    const left = pausedRemainingRef.current;
    if (left <= 0) { fireExpire(); return; }
    arm(left);
  }, [arm, fireExpire, isRunning]);

  const stop = useCallback(() => {
    clearHandles();
    expiredRef.current = true; // остановка вручную не должна потом выстрелить onExpire
    runningRef.current = false;
    setIsRunning(false);
  }, [clearHandles]);

  const reset = useCallback((nextDuration) => {
    clearHandles();
    const ms = typeof nextDuration === 'number' ? nextDuration : durationRef.current;
    durationRef.current = ms;
    pausedRemainingRef.current = ms;
    expiredRef.current = false;
    runningRef.current = false;
    deadlineRef.current = 0;
    setIsRunning(false);
    setIsFinished(false);
    setRemainingMs(ms);
  }, [clearHandles]);

  // Возврат на вкладку: интервал мог не отработать, пересчитываем сразу,
  // иначе цифры "догоняют" реальность рывком, а истёкшая проба висит.
  useEffect(() => {
    const onVisible = () => { if (isRunning && document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check, isRunning]);

  useEffect(() => {
    if (autoStart) start(duration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // StrictMode в dev монтирует эффекты дважды: mount -> cleanup -> mount.
  // Cleanup гасит интервал и таймаут уже ЗАПУЩЕННОГО таймера, а второй
  // проход его не перезаводит — таймер визуально замирает, хотя дедлайн
  // ещё в будущем. Именно так и выглядит "таймер работает нестабильно".
  // Поэтому при повторном монтировании восстанавливаем отсчёт по
  // сохранённому дедлайну: время-то идёт по Date.now(), а не по тикам.
  useEffect(() => {
    if (runningRef.current && !intervalRef.current) {
      const left = deadlineRef.current - Date.now();
      if (left > 0) arm(left);
      else fireExpire();
    }
    return clearHandles;
  }, [clearHandles, arm, fireExpire]);

  const total = durationRef.current || 1;
  const elapsedMs = Math.max(0, total - remainingMs);

  return {
    remainingMs,
    remainingSec: Math.ceil(remainingMs / 1000),
    elapsedMs,
    percent: Math.min(100, Math.max(0, (elapsedMs / total) * 100)),
    isRunning,
    isFinished,
    start,
    pause,
    resume,
    stop,
    reset,
  };
}

export default useTimerState;
